import { useState, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import { useGetGradesForStudentQuery } from '@school_ify/schoolify-repository/src/student/types';

import './PeriodsTable.scss';

import i18n from 'translations/i18n';
import { useAppSelector } from 'redux/hooks';
import { getUser } from 'redux/authentication/auth.slice';

import StudentGradeCell from './components/StudentGradeCell/StudentGradeCell';
import getGradeName from './components/helper';

const PeriodsTable: React.FC = () => {
  const user = useAppSelector(getUser);
  const { data, loading } = useGetGradesForStudentQuery({ variables: { studentId: user.id } });

  const gradeItems = (data?.view_grades_for_students || [])
    .filter(item => item.subject?.id)
    .filter(item => item.assignment.type !== 'MATERIAL');

  const subjects: { id: string; name: string }[] = [];
  gradeItems.forEach(item => {
    const id = item?.subject?.id;
    if (!subjects.find(item => item.id === id) && id !== undefined) {
      subjects.push({ id, name: item?.subject?.name });
    }
  });

  const dateItems: {
    date: string;
    subjects: { subjectId: string; ids: string[] }[];
    cellCount: number;
  }[] = [];

  const gradeItemsSorted = gradeItems.sort((a: any, b: any) => {
    if (a?.assignment?.due_at && b?.assignment?.due_at) {
      return (
        DateTime.fromISO(a.assignment.due_at).toUnixInteger() -
        DateTime.fromISO(b.assignment.due_at).toUnixInteger()
      );
    }
    return a;
  });

  gradeItemsSorted.forEach(item => {
    const date = DateTime.fromISO(item.assignment.due_at).toFormat('y-MM-dd');

    if (!dateItems.find(subjectDate => subjectDate.date === date)) {
      dateItems.push({ date, subjects: [], cellCount: 0 });
    }
  });

  gradeItems.forEach(item => {
    const date = DateTime.fromISO(item.assignment.due_at).toFormat('y-MM-dd');
    const subjectDate = dateItems.find(i => i.date === date);
    const subjectId = item?.subject?.id;

    const existingItem = subjectDate.subjects.find(subject => subject.subjectId === subjectId);

    if (existingItem) {
      existingItem.ids.push(item.id);
    } else {
      subjectDate.subjects.push({ ids: [item.id], subjectId });
    }
  });
  dateItems.forEach(dateItem => {
    const maxCellsPerDate = Math.max(...dateItem.subjects.map(subject => subject.ids.length));
    dateItem.cellCount = maxCellsPerDate;
  });

  const [popoverOpened, setPopoverOpened] = useState('');

  const scrollContainer = useRef(null);
  const { t } = useTranslation();

  const handleTableScroll = () => {
    if (popoverOpened !== '') {
      setPopoverOpened('');
    }
  };

  if (loading) {
    return null;
  }

  return (
    <div className="student-grade-periods">
      <div className="subject-title">{}</div>
      {subjects.length ? (
        <div ref={scrollContainer} onScroll={handleTableScroll} className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th></th>
                {dateItems.map(dateItem => {
                  const arr = Array(dateItem.cellCount).fill(1);
                  return arr.map((dateItemId, index) => {
                    return (
                      <th key={index}>
                        <div>
                          {DateTime.fromISO(dateItem.date)
                            .setLocale(i18n.language)
                            .toFormat('dd.MM')}
                        </div>
                      </th>
                    );
                  });
                })}
                <th>{t('grades:average')}</th>
              </tr>
            </thead>

            <tbody>
              {subjects.map(subject => {
                let gradeCount = 0;
                let gradeSum = 0;
                return (
                  <tr key={subject.id}>
                    <td>{subject.name}</td>
                    {dateItems.map(dateItem => {
                      const dateItemSubject = dateItem.subjects.find(
                        item => item.subjectId === subject.id
                      );

                      const arr = Array(dateItem.cellCount).fill(1);
                      return arr.map((id2, index) => {
                        const id = (dateItemSubject?.ids || [])[index];
                        if (!id) {
                          return <td key={index}></td>;
                        }
                        const gradeItem = gradeItems.find(i => i.id === id);
                        gradeSum += gradeItem?.mark?.value || 0;
                        gradeCount += gradeItem?.mark ? 1 : 0;
                        return (
                          <StudentGradeCell
                            id={id}
                            key={id}
                            gradeItem={gradeItem}
                            popoverOpened={popoverOpened}
                            setPopoverOpened={setPopoverOpened}
                          />
                        );
                      });
                    })}

                    <td>{getGradeName((gradeSum / gradeCount).toFixed(0))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="assignments-missing">{t('grades:No_assignments')}</div>
      )}
    </div>
  );
};

export default PeriodsTable;
